import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiError, api } from './api';
import {
  AUTH_SWITCH_BRAND_API_PATH,
  type Brand,
  HOSTNAME_LOOKUP_API_PATH,
  type HostnameBrandWire,
  type Me,
  type MeWire,
  type RefreshWire,
  switchBrandBody,
  toHostnameBrand,
  toMe,
} from './brand-wire';

export type { Brand, BrandKind, MembershipOrigin } from './brand-wire';

interface AuthState {
  ready: boolean;
  /**
   * THE LIVE TOKEN, READ AT CALL TIME. Deliberately a getter and not a value.
   *
   * It used to be reactive state on this context, and the 12-minute rotation
   * therefore changed a string that ~40 effects across 31 files listed in their
   * dependency arrays. Every rotation re-ran all of them, so the whole app
   * refetched itself while the user sat on a page -- what Jay reported on
   * 2026-09-08 as "it refreshes itself once every few minutes".
   *
   * A getter cannot be a dependency, so that defect is now unrepresentable
   * rather than merely absent: an effect keyed on the token no longer compiles.
   * Call it inside the request, never in render, so the value is always current.
   */
  getAccessToken: () => string | null;
  /**
   * What a data hook SHOULD key on: identity of the session, not the token.
   * Changes on sign-in, sign-out and brand switch -- the three moments a page's
   * data genuinely is different -- and NOT on rotation.
   */
  sessionKey: string | null;
  me: Me | null;
  signOut: () => Promise<void>;
  /**
   * Returns the NEW access token, or null if the refresh failed.
   *
   * It returned a boolean, which forced every 401 handler to refresh, give up,
   * and wait for a re-render to retry with the new token. That in turn forced
   * data hooks to depend on the token STRING, which made them re-fetch on the
   * 12-minute rotation too. Handing the token back lets the retry happen inline
   * and lets the hooks depend on identity instead.
   */
  refresh: () => Promise<string | null>;
  reloadMe: () => Promise<void>;
  switchBrand: (brandId: string) => Promise<void>;
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ requiresTotp: true; challengeId: string } | { requiresTotp: false }>;
  completeTotpChallenge: (challengeId: string, code: string) => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  // The token lives in a ref so that rotating it touches NOTHING reactive.
  const tokenRef = useRef<string | null>(null);
  const getAccessToken = useCallback(() => tokenRef.current, []);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * A rotation updates the ref and stops. A NEW session additionally mints a new
   * sessionKey, which is what data hooks watch. The distinction between those
   * two is the whole fix.
   */
  const setToken = useCallback((token: string | null, opts?: { newSession?: boolean }) => {
    tokenRef.current = token;
    if (token === null) {
      setSessionKey(null);
      return;
    }
    if (opts?.newSession) setSessionKey(`s${Date.now()}:${Math.random().toString(36).slice(2)}`);
    else setSessionKey((k) => k ?? `s${Date.now()}`);
  }, []);

  const scheduleRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh ~12 minutes in (access token TTL is 15 min).
    refreshTimerRef.current = setTimeout(
      () => {
        void refresh();
      },
      12 * 60 * 1000,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async (): Promise<string | null> => {
    try {
      const r = await api<RefreshWire>('/auth/refresh', { method: 'POST', body: {} });
      // NO newSession: a rotation is the same session with a fresher token, so
      // sessionKey is untouched and nothing above this re-runs.
      setToken(r.accessToken);
      scheduleRefresh();
      return r.accessToken;
    } catch {
      setToken(null);
      setMe(null);
      return null;
    }
  }, [scheduleRefresh, setToken]);

  const reloadMe = useCallback(async () => {
    const accessToken = tokenRef.current;
    if (!accessToken) {
      setMe(null);
      return;
    }
    try {
      const m = await api<MeWire>('/auth/me', { accessToken });
      setMe(toMe(m));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // Token expired between bootstrap and /me. Refresh, then RETRY INLINE.
        //
        // This used to rely on "the post-refresh accessToken effect will
        // retrigger reloadMe" — true only while a rotation changed reactive
        // state. It no longer does, by design, so the retry has to be explicit
        // or a 401 here would refresh the token and then never fetch /me,
        // leaving a signed-in user staring at a loading screen. This is exactly
        // what refresh() returning the new token was added for.
        const fresh = await refresh();
        if (!fresh) {
          setToken(null);
          setMe(null);
          return;
        }
        try {
          setMe(toMe(await api<MeWire>('/auth/me', { accessToken: fresh })));
        } catch {
          setToken(null);
          setMe(null);
        }
      } else {
        // Network failure or other transient error: clear state so the user
        // bounces to /login rather than getting stuck on a "Loading…" screen.
        setToken(null);
        setMe(null);
      }
    }
    // Stable: reads the token from the ref, so rotation cannot change its
    // identity and cannot re-trigger the effect below.
  }, [refresh, setToken]);

  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', body: {}, accessToken: tokenRef.current });
    } catch {
      /* best effort */
    }
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    setToken(null);
    setMe(null);
  }, [setToken]);

  const switchBrand = useCallback(
    async (brandId: string) => {
      if (!tokenRef.current) return;
      const r = await api<RefreshWire>(AUTH_SWITCH_BRAND_API_PATH, {
        body: switchBrandBody(brandId),
        accessToken: tokenRef.current,
      });
      // A brand switch IS a new scope, so the sessionKey changes here and data
      // hooks re-run — which is correct, unlike on rotation. The explicit
      // reloadMe that used to follow is gone: it ran with the OLD token from a
      // stale closure and the effect below then fetched /me a second time.
      setToken(r.accessToken, { newSession: true });
    },
    [setToken],
  );

  const signInWithPassword = useCallback<AuthState['signInWithPassword']>(
    async (email, password) => {
      const r = await api<RefreshWire & { requiresTotp?: true; challengeId?: string }>(
        '/auth/login-password',
        { method: 'POST', body: { email, password } },
      );
      if (r.requiresTotp && r.challengeId) {
        return { requiresTotp: true, challengeId: r.challengeId };
      }
      setToken(r.accessToken, { newSession: true });
      scheduleRefresh();
      return { requiresTotp: false };
    },
    [scheduleRefresh, setToken],
  );

  const completeTotpChallenge = useCallback(
    async (challengeId: string, code: string) => {
      const r = await api<RefreshWire>('/auth/totp/challenge', {
        method: 'POST',
        body: { challengeId, code },
      });
      setToken(r.accessToken, { newSession: true });
      scheduleRefresh();
    },
    [scheduleRefresh, setToken],
  );

  // Bootstrap: try to refresh on mount.
  useEffect(() => {
    void (async () => {
      await refresh();
      setReady(true);
    })();
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [refresh]);

  // Fetch /me when the SESSION changes — sign-in, sign-out, brand switch. Not
  // on rotation: keying this on the token string is what refetched /me every
  // twelve minutes and made every consumer of this context re-render with it.
  useEffect(() => {
    if (sessionKey) void reloadMe();
  }, [sessionKey, reloadMe]);

  // Hostname auto-switch scopes the session when the API maps this host to a brand.
  const [hostnameSyncDone, setHostnameSyncDone] = useState(false);
  useEffect(() => {
    if (hostnameSyncDone) return;
    if (!me?.user || !sessionKey) return;
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    if (!host || host === 'app.adholics.net' || host === 'localhost') {
      setHostnameSyncDone(true);
      return;
    }
    void (async () => {
      try {
        const r = toHostnameBrand(
          await api<HostnameBrandWire>(
            `${HOSTNAME_LOOKUP_API_PATH}?hostname=${encodeURIComponent(host)}`,
          ),
        );
        if (r.brand && me.brands.some((b) => b.brandId === r.brand!.brandId)) {
          if (me.activeBrandId !== r.brand.brandId) {
            await switchBrand(r.brand.brandId);
          }
        }
      } catch {
        /* hostname lookup failure is non-fatal */
      } finally {
        setHostnameSyncDone(true);
      }
    })();
  }, [me, sessionKey, hostnameSyncDone, switchBrand]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      getAccessToken,
      sessionKey,
      me,
      signOut,
      refresh,
      reloadMe,
      switchBrand,
      signInWithPassword,
      completeTotpChallenge,
    }),
    [
      ready,
      getAccessToken,
      sessionKey,
      me,
      signOut,
      refresh,
      reloadMe,
      switchBrand,
      signInWithPassword,
      completeTotpChallenge,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
